import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled, { css, keyframes } from 'styled-components';

import { Target } from '../../../../client';
import AnimatedDigitsLabel from '../../../../components/AnimatedDigitsLabel';
import SpinePlayer from '../../../../components/SpinePlayer';
import Formatter from '../../../../i18n/formatter';
import { getCurrency, __ } from '../../../../i18n/l10n';
import SoundPlayer from '../../../../singletons/SoundPlayer';
import { SpineAnimationId, SpineAnimationIds } from '../../../../types/animations';
import { SoundIds } from '../../../../types/sounds';
import useGRHomeContext from '../../hooks/useGRHomeContext';
import { GRFullScreenNotifications } from '../../types/notification';
import GRButton from '../GRButton/GRButton';

const SpineMultiplicatorAnimationIdByMultiplicator = {
  '1': SpineAnimationIds.GR_FREEBETS_WON_X1,
  '2': SpineAnimationIds.GR_FREEBETS_WON_X2,
  '5': SpineAnimationIds.GR_FREEBETS_WON_X5,
  '10': SpineAnimationIds.GR_FREEBETS_WON_X10,
  '20': SpineAnimationIds.GR_FREEBETS_WON_X20,
};

const appearAnim = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

type WrapperProps = {
  $show: boolean;
};

const Wrapper = styled.div<WrapperProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;

  display: flex;
  justify-content: center;
  align-items: center;

  background: radial-gradient(transparent, #00000099);

  &.gr-round-complete-notification-appearance-transition-enter {
    animation: ${appearAnim} 300ms ease-in-out forwards;
  }
  &.gr-round-complete-notification-appearance-transition-exit {
    animation: ${appearAnim} 300ms ease-in-out forwards reverse;
  }

  ${({ $show }) =>
    !$show
      ? css`
          pointer-events: none;
        `
      : undefined}
`;

const Labels = styled.div`
  position: relative;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  margin-top: -10%;
  filter: drop-shadow(0 0 0.3em #00000077);
`;

const CongratsLabel = styled.div`
  text-align: center;
  font-size: 3em;
`;

const WinFreebetsLabel = styled.div`
  text-align: center;
  font-size: 1.6em;
`;

const FreebetsAmountWonWrapper = styled.div`
  position: relative;
  width: 70%;
  text-align: center;
  margin-top: 1.5em;
`;

type FreebetsAmountWonProps = {
  $isFinal: boolean;
  $show: boolean;
};

const FreebetsAmountWon = styled(AnimatedDigitsLabel)<FreebetsAmountWonProps>`
  position: absolute;
  width: 77%;
  left: 8%;

  font-size: 3em;
  transition: none 100ms ease-in-out;
  transition-property: color, filter;
  opacity: 0;

  ${({ $isFinal }) => css`
    ${$isFinal
      ? css`
          color: white;
          filter: drop-shadow(0 0 0.1em #00000099);
        `
      : css`
          color: #00000055;
        `}
  `}

  ${({ $show }) =>
    $show
      ? css`
          animation: ${appearAnim} 300ms ease-in-out forwards;
        `
      : undefined}
`;

const StyledSpinePlayer = styled(SpinePlayer)`
  position: absolute;

  top: 0;
  left: 0;
  z-index: -1;

  &.gr-claim-button-appearance-transition-enter {
    animation: ${appearAnim} 300ms ease-in-out forwards;
  }
  &.gr-claim-button-appearance-transition-exit {
    animation: ${appearAnim} 300ms ease-in-out forwards reverse;
  }

  /* We eventually should fix this nightmare by recentering the animation around the blue text holder */
  ${Target.__MOBILE__
    ? css`
        transform: translate(6%, -26%) scale(1.3);
      `
    : css`
        transform: translate(6%, -30%) scale(1.1);
      `}
`;

type StyledButtonProps = {
  $show: boolean;
};

const StyledButton = styled(GRButton)<StyledButtonProps>`
  position: absolute;
  bottom: 1em;

  ${({ $show }) =>
    !$show
      ? css`
          pointer-events: none;
        `
      : undefined}
`;

type GRRoundCompleteNotificationProps = {
  className?: string;
  baseFreebetsAmount: number;
  multiplicator: number;
  totalFreebetsAmount: number;
  onClaimRewardClick: () => void;
};

const GRRoundCompleteNotification = ({
  className = '',
  baseFreebetsAmount,
  multiplicator = 1,
  totalFreebetsAmount,
  onClaimRewardClick,
}: GRRoundCompleteNotificationProps) => {
  const { fullScreenNotifShown } = useGRHomeContext();
  const show = useMemo(() => fullScreenNotifShown === GRFullScreenNotifications.ROUND_COMPLETE, [fullScreenNotifShown]);

  const [nextValue, setNextValue] = useState<number>();
  const baseFreebetsAmountRef = useRef(baseFreebetsAmount);
  const totalFreebetsAmountRef = useRef<number>(totalFreebetsAmount);
  const totalFreebetsAmountTimeoutIdRef = useRef<NodeJS.Timeout | null>();
  const [isFinal, setIsFinal] = useState(false);

  const [showSpineAnimation, setShowSpineAnimation] = useState(false);
  const showSpineAnimationTimeoutIdRef = useRef<NodeJS.Timeout | null>();

  const [showAnimatedAmount, setShowAnimatedAmount] = useState(false);
  const showAnimatedAmountTimeoutIdRef = useRef<NodeJS.Timeout | null>();
  // @ts-ignore
  const animationId: SpineAnimationId = SpineMultiplicatorAnimationIdByMultiplicator[multiplicator.toString()];
  const bluetagSoundTimeoutIdRef = useRef<NodeJS.Timeout | null>();
  const multiplicatorWheelSoundTimeoutIdRef = useRef<NodeJS.Timeout | null>();

  useEffect(() => {
    baseFreebetsAmountRef.current = baseFreebetsAmount;
  }, [baseFreebetsAmount]);

  useEffect(() => {
    totalFreebetsAmountRef.current = totalFreebetsAmount;
  }, [totalFreebetsAmount]);

  useEffect(() => {
    setNextValue((prev) => (prev ? prev : baseFreebetsAmount));
  }, [baseFreebetsAmount]);

  useEffect(() => {
    if (show) {
      SoundPlayer.load(SoundIds.GR_WIN_TICK).then((s) => {
        s.play();
      });

      if (!showSpineAnimationTimeoutIdRef.current) {
        showSpineAnimationTimeoutIdRef.current = setTimeout(() => {
          setShowSpineAnimation(true);
        }, 1200);
      }

      if (!bluetagSoundTimeoutIdRef.current) {
        bluetagSoundTimeoutIdRef.current = setTimeout(() => {
          SoundPlayer.load(SoundIds.GR_WIN_BLUE_TAG_MOVING).then((s) => {
            s.play();
          });
        }, 1600);
      }

      if (!showAnimatedAmountTimeoutIdRef.current) {
        showAnimatedAmountTimeoutIdRef.current = setTimeout(() => {
          setShowAnimatedAmount(true);
        }, 2300);
      }

      if (!multiplicatorWheelSoundTimeoutIdRef.current) {
        multiplicatorWheelSoundTimeoutIdRef.current = setTimeout(() => {
          SoundPlayer.load(SoundIds.GR_WIN_MULTIPLICATOR_WHEEL).then((s) => {
            s.play();
          });
        }, 4600);
      }

      if (!totalFreebetsAmountTimeoutIdRef.current)
        totalFreebetsAmountTimeoutIdRef.current = setTimeout(() => {
          setNextValue(totalFreebetsAmountRef.current);
          setIsFinal(true);

          if (baseFreebetsAmountRef.current !== totalFreebetsAmountRef.current) {
            SoundPlayer.load(SoundIds.GR_WIN_TOTAL_FREEBETS_AMOUNT_INCREMENT).then((s) => {
              s.play();
            });
          }
        }, 7200);
    }
  }, [show]);

  useEffect(() => {
    return () => {
      const showSpineAnimationTimeoutId = showSpineAnimationTimeoutIdRef.current;
      const totalFreebetsAmountTimeoutId = totalFreebetsAmountTimeoutIdRef.current;
      const showAnimatedAmountTimeoutId = showAnimatedAmountTimeoutIdRef.current;
      const bluetagSoundTimeoutId = bluetagSoundTimeoutIdRef.current;
      const multiplicatorWheelSoundTimeoutId = multiplicatorWheelSoundTimeoutIdRef.current;

      SoundPlayer.load(SoundIds.GR_WIN_TICK).then((s) => {
        s.stop();
      });
      SoundPlayer.load(SoundIds.GR_WIN_BLUE_TAG_MOVING).then((s) => {
        s.stop();
      });
      SoundPlayer.load(SoundIds.GR_WIN_MULTIPLICATOR_WHEEL).then((s) => {
        s.stop();
      });
      SoundPlayer.load(SoundIds.GR_WIN_TOTAL_FREEBETS_AMOUNT_INCREMENT).then((s) => {
        s.stop();
      });

      if (showSpineAnimationTimeoutId) {
        clearTimeout(showSpineAnimationTimeoutId);
        showSpineAnimationTimeoutIdRef.current = null;
      }

      if (totalFreebetsAmountTimeoutId) {
        clearTimeout(totalFreebetsAmountTimeoutId);
        totalFreebetsAmountTimeoutIdRef.current = null;
      }

      if (showAnimatedAmountTimeoutId) {
        clearTimeout(showAnimatedAmountTimeoutId);
        showAnimatedAmountTimeoutIdRef.current = null;
      }

      if (bluetagSoundTimeoutId) {
        clearTimeout(bluetagSoundTimeoutId);
        bluetagSoundTimeoutIdRef.current = null;
      }

      if (multiplicatorWheelSoundTimeoutId) {
        clearTimeout(multiplicatorWheelSoundTimeoutId);
        multiplicatorWheelSoundTimeoutIdRef.current = null;
      }
    };
  }, []);

  const formatAmount = useCallback((value: number) => Formatter.formatBankroll(value * 100, getCurrency()), []);
  const lastFormatAmount = useCallback(
    (value: number) => Formatter.formatBankroll(value * 100, getCurrency(), (value * 100) % 100 === 0 || value >= 1000),
    [],
  );

  return (
    <CSSTransition
      in={show}
      classNames="gr-round-complete-notification-appearance-transition"
      timeout={300}
      mountOnEnter
      unmountOnExit
    >
      <Wrapper className={className} $show={show}>
        <Labels>
          <CongratsLabel>{__('Bravo!')}</CongratsLabel>
          <WinFreebetsLabel>{__('You win Freebets')}</WinFreebetsLabel>
          <FreebetsAmountWonWrapper>
            {/* @ts-ignore */}
            <FreebetsAmountWon
              current={baseFreebetsAmount}
              next={nextValue || baseFreebetsAmount}
              formatFn={formatAmount}
              lastFormatFn={lastFormatAmount}
              $isFinal={isFinal}
              $show={showAnimatedAmount}
            />
            <CSSTransition
              in={showSpineAnimation}
              classNames="gr-round-complete-spine-animation-appearance-transition"
              timeout={300}
              mountOnEnter
              unmountOnExit
            >
              <StyledSpinePlayer animationId={animationId} width={600} height={600} hideMuteButton />
            </CSSTransition>
          </FreebetsAmountWonWrapper>
        </Labels>
        <CSSTransition
          in={isFinal}
          classNames="gr-claim-button-appearance-transition"
          timeout={300}
          mountOnEnter
          unmountOnExit
        >
          <StyledButton $show={isFinal} onClick={onClaimRewardClick}>
            {__('Claim')}
          </StyledButton>
        </CSSTransition>
      </Wrapper>
    </CSSTransition>
  );
};

export default GRRoundCompleteNotification;
